import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {AxiosError} from 'axios';
import {ToastType} from '../../../../shared/constants';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import UpsertAnnotationDialog, {
  IAnnotationForm,
} from '../upsert-annotation-dialog/UpsertAnnotationDialog';
import './UpsertAnnotationWrapper.scss';
import {
  useAnnotationDetails,
  useUpsertAnnotation,
} from '../../../../hooks/document/useDocument';
import {SubmitHandler} from 'react-hook-form';
import {useQueryClient} from '@tanstack/react-query';
import {isEmpty} from '../../../../utils/formatters';
import {t} from 'i18next';

export interface UpsertAnnotationWrapperProps {
  showModal?: boolean;
  annotationID?: number;
  securityGroupId?: number;
  onUpsertAnnotation?: () => void;
  onClose?: (showModal: boolean) => void;
}

const UpsertAnnotationWrapper: React.FC<UpsertAnnotationWrapperProps> = ({
  showModal,
  annotationID: annotationId = 0,
  securityGroupId: securityGroupID = 0,
  onUpsertAnnotation,
  onClose,
}: UpsertAnnotationWrapperProps) => {
  const [showToast, setShowToast] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [upsertAnnotationSuccessMessage, setUpsertAnnotationSuccessMessage] =
    useState<string>('');
  const [upsertAnnotationError, setUpsertAnnotationError] =
    useState<string>('');
  const [showUpsertAnnotationDialog, setShowUpsertAnnotationDialog] =
    useState<boolean>(showModal ?? false);

  const queryClient = useQueryClient();
  const {mutate: addAnnotation} = useUpsertAnnotation();
  const {data: annotationDetailsResponse, refetch: refetchAnnotationDetails} =
    useAnnotationDetails({annotationId});

  const isAnnotationEdited = useMemo(() => {
    return annotationId !== 0 && !isEmpty(annotationId);
  }, [annotationId]);

  useEffect(() => {
    setShowUpsertAnnotationDialog(showModal ?? false);
  }, [showModal, onClose]);

  useEffect(() => {
    if (
      showModal &&
      isAnnotationEdited &&
      annotationId !== 0 &&
      !isEmpty(annotationId)
    ) {
      refetchAnnotationDetails();
    }
  }, [
    showModal,
    annotationId,
    refetchAnnotationDetails,
    queryClient,
    isAnnotationEdited,
  ]);

  useEffect(() => {
    if (!showUpsertAnnotationDialog && isAnnotationEdited) {
      queryClient.setQueryData(['useAnnotationDetails', {annotationId}], null);
    }
  }, [
    annotationId,
    isAnnotationEdited,
    queryClient,
    showUpsertAnnotationDialog,
  ]);

  useEffect(() => {
    !showUpsertAnnotationDialog && onClose?.(false);
  }, [onClose, showUpsertAnnotationDialog]);

  const onSuccessAddAnnotation = useCallback(() => {
    onUpsertAnnotation?.();
    setLoading(false);
    setUpsertAnnotationSuccessMessage(
      t(
        `annotationLayer.addEditAnnotation.${isAnnotationEdited ? 'editSuccess' : 'addSuccess'}`
      )
    );
    setShowUpsertAnnotationDialog(false);
    setShowToast(true);
  }, [onUpsertAnnotation, isAnnotationEdited]);

  const onErrorAddAnnotation = useCallback((error: Error) => {
    const errorData: any = (error as AxiosError)?.response?.data;
    setUpsertAnnotationError(
      errorData?.detail?.split('\n')?.[0] ?? errorData?.title ?? 'api-error'
    );
    setLoading(false);
  }, []);

  const handleUpsertAnnotation: SubmitHandler<IAnnotationForm> = useCallback(
    (formData: IAnnotationForm) => {
      setUpsertAnnotationSuccessMessage('');
      setUpsertAnnotationError('');
      setLoading(true);
      addAnnotation(
        {
          isAnnotationEdited,
          securityGroupID,
          annotationID: annotationId,
          ...formData,
        },
        {
          onSuccess: onSuccessAddAnnotation,
          onError: onErrorAddAnnotation,
        }
      );
    },
    [
      addAnnotation,
      annotationId,
      isAnnotationEdited,
      securityGroupID,
      onSuccessAddAnnotation,
      onErrorAddAnnotation,
    ]
  );

  const onCloseUpsertAnnotationDialog = useCallback(() => {
    setShowUpsertAnnotationDialog(false);
    setUpsertAnnotationError('');
  }, []);

  const onCloseToast = useCallback(() => {
    setShowToast(false);
    setUpsertAnnotationError('');
    setUpsertAnnotationSuccessMessage('');
  }, []);

  return (
    <>
      <UpsertAnnotationDialog
        annotationModel={
          isAnnotationEdited
            ? annotationDetailsResponse?.annotationsModel
            : undefined
        }
        isAnnotationEdited={isAnnotationEdited}
        showModal={showUpsertAnnotationDialog}
        annotationNameError={upsertAnnotationError}
        requestLoading={loading}
        onSave={handleUpsertAnnotation}
        onClose={onCloseUpsertAnnotationDialog}
      />
      {showToast && (
        <ArrToastNotification
          message={upsertAnnotationSuccessMessage}
          status={[ToastType.Success]}
          onClose={onCloseToast}
        />
      )}
    </>
  );
};

export default UpsertAnnotationWrapper;
